import { parseCookie, stringifySetCookie } from 'cookie';

import type { AuthMethod } from '@webcatalog/constants/auth-methods';

import {
  ACCESS_TOKEN_KEY,
  ACCESS_TOKEN_UPDATED_AT,
  CODE_VERIFIER_KEY,
  REFRESH_TOKEN_COOKIE_KEY,
} from './constants/storage-keys';
import { createPkcePairAsync } from './create-pkce-pair-async';
import { decodeBase64Json } from './utils/decode-base64-json';
import { encodeBase64Json } from './utils/encode-base64-json';
import { isJwtExpired } from './utils/is-jwt-expired';
import { parseUrl } from './utils/parse-url';

const VERIFIER_TTL_SECONDS = 60 * 15; // 15 minutes — only needs to survive the auth round-trip.
const REFRESH_TOKEN_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;
const ACCESS_TOKEN_REFRESH_AFTER_MS = 30 * 60 * 1000;

const postJsonAsync = (url: string, body: unknown) =>
  fetch(url, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });

export class AuthClient {
  private readonly apiUrl: string;
  private readonly authUrl: string;
  private readonly clientId: string;
  private readonly cookieDomain: string | null;

  constructor({
    apiUrl,
    authUrl,
    clientId,
    cookieDomain,
  }: {
    /** The API, which serves the token endpoints. */
    apiUrl: string;
    /** The sign-in UI the browser is sent to. */
    authUrl: string;
    /** OAuth 2.0 client_id (RFC 6749 §2.2) identifying the calling app. */
    clientId: string;
    /** Parent domain the refresh token is shared on (e.g. `.webcatalog.io`),
     * used only while the page is actually served from it. */
    cookieDomain: string | null;
  }) {
    this.apiUrl = apiUrl;
    this.authUrl = authUrl;
    this.clientId = clientId;
    this.cookieDomain = cookieDomain;
  }

  async isAuthedAsync() {
    return Boolean(await this.getRefreshTokenAsync());
  }

  async getAccessTokenAsync(): Promise<string> {
    const { accessToken: cachedAccessToken, accessTokenUpdatedAt } =
      this.getCachedAccessToken();

    if (
      !cachedAccessToken ||
      (accessTokenUpdatedAt &&
        Date.now() - Date.parse(accessTokenUpdatedAt) >
          ACCESS_TOKEN_REFRESH_AFTER_MS)
    ) {
      const newAccessToken = await this.refreshAccessTokenAsync();
      if (newAccessToken) {
        return newAccessToken;
      }
    }

    if (!cachedAccessToken) {
      throw new Error('Failed to get access token from API or cache.');
    }

    if (isJwtExpired(cachedAccessToken)) {
      this.setCachedAccessToken(null);
      throw new Error('Cached access token has expired.');
    }

    return cachedAccessToken;
  }

  async refreshAccessTokenAsync(): Promise<string | null> {
    const refreshToken = await this.getRefreshTokenAsync();
    if (!refreshToken) {
      return null;
    }

    let response: Response;
    try {
      response = await postJsonAsync(
        `${this.apiUrl}/api/auth/v1/request-access-token`,
        { refreshToken },
      );
    } catch {
      return null;
    }

    if (!response.ok) {
      // The status alone is not enough: some networks (e.g. VPNs) block auth
      // requests with their own 401, which must not log the user out.
      const body = (await response.json().catch(() => null)) as {
        message?: unknown;
      } | null;
      if (
        response.status === 401 &&
        body?.message === 'INVALID_OR_EXPIRED_REFRESH_TOKEN'
      ) {
        await this.logOutAsync();
      }
      return null;
    }

    const { accessToken } = (await response.json()) as { accessToken: string };
    this.setCachedAccessToken(accessToken);

    return accessToken;
  }

  async getRefreshTokenAsync() {
    // SSR apps (marketing's tRPC client) can reach this on the server, where
    // the visitor is treated as signed out.
    if (typeof document === 'undefined') {
      return undefined;
    }

    return parseCookie(document.cookie)[REFRESH_TOKEN_COOKIE_KEY];
  }

  async setRefreshTokenAsync(refreshToken: string) {
    document.cookie = stringifySetCookie({
      name: REFRESH_TOKEN_COOKIE_KEY,
      value: refreshToken,
      domain: this.getCookieDomain(),
      path: '/',
      maxAge: REFRESH_TOKEN_MAX_AGE_SECONDS,
      secure: true,
    });
  }

  /**
   * Drops the cookie without calling the API. For a caller that has already
   * been told the token is dead — a real log-out goes through `logOutAsync`.
   */
  async deleteRefreshTokenAsync() {
    // Clear both possible scopes: the shared domain (e.g. cookies set by the
    // web flow on `.webcatalog.io`) and host-only (e.g. cookies seeded by
    // Electron desktop sessions on `atlas.webcatalog.io`). Cookie deletion
    // only matches a single domain/path tuple, so we issue both.
    const domain = this.getCookieDomain();
    if (domain) {
      document.cookie = stringifySetCookie({
        name: REFRESH_TOKEN_COOKIE_KEY,
        value: '',
        domain,
        path: '/',
        maxAge: 0,
      });
    }
    document.cookie = stringifySetCookie({
      name: REFRESH_TOKEN_COOKIE_KEY,
      value: '',
      path: '/',
      maxAge: 0,
    });
  }

  /**
   * @param callbackUrl absolute URL of the auth callback route on the calling app
   *   (e.g. `https://webcatalog.io/api/auth/callback/`). The auth app redirects back
   *   here after authentication.
   * @param redirectPath same-origin path to send the user to after the callback.
   */
  async getLoginUrlAsync(
    callbackUrl: string,
    redirectPath: string,
    opts?: { type?: 'login' | 'signup'; method?: AuthMethod },
  ) {
    // The callback route reads the verifier back from this cookie and
    // consumes it at the exchange.
    const { codeVerifier, codeChallenge } = await createPkcePairAsync();
    document.cookie = stringifySetCookie({
      name: CODE_VERIFIER_KEY,
      value: codeVerifier,
      path: '/',
      secure: true,
      sameSite: 'lax',
      maxAge: VERIFIER_TTL_SECONDS,
    });

    const authUrlObj = new URL(this.authUrl);
    authUrlObj.searchParams.set('client_id', this.clientId);
    authUrlObj.searchParams.set('redirect_uri', callbackUrl);
    authUrlObj.searchParams.set('code_challenge', codeChallenge);
    if (opts?.type) {
      authUrlObj.searchParams.set('type', opts.type);
    }
    if (opts?.method) {
      authUrlObj.searchParams.set('method', opts.method);
    }
    authUrlObj.searchParams.set('state', encodeBase64Json({ redirectPath }));

    return authUrlObj.href;
  }

  /**
   * Completes the auth callback in the browser. Reads and consumes the PKCE
   * verifier cookie, exchanges the auth code for a refresh token when the
   * shared-domain cookie isn't already present, and returns the same-origin
   * path to continue to.
   *
   * Use this on the client route registered as the `callbackUrl` passed to
   * `getLoginUrlAsync` (e.g. `/auth/callback`).
   */
  async completeAuthCallbackAsync(options: {
    code?: string;
    state?: string;
  }): Promise<string> {
    const { code, state } = options;
    // No state means no destination was carried; fall through to the default.
    const decodedState = state ? decodeBase64Json(state) : null;
    const redirectPath =
      decodedState &&
      typeof decodedState === 'object' &&
      'redirectPath' in decodedState &&
      typeof decodedState.redirectPath === 'string'
        ? decodedState.redirectPath
        : undefined;

    // Production fast path: the auth app wrote the refresh token to the wildcard
    // cookie domain, which this host can read. No exchange needed.
    const existingRefreshToken = await this.getRefreshTokenAsync();

    // Read and consume the PKCE verifier cookie set by `getLoginUrlAsync`.
    // Delete it either way so it doesn't linger.
    const codeVerifier = parseCookie(document.cookie)[CODE_VERIFIER_KEY];
    if (codeVerifier) {
      document.cookie = stringifySetCookie({
        name: CODE_VERIFIER_KEY,
        value: '',
        path: '/',
        maxAge: 0,
      });
    }

    if (!existingRefreshToken) {
      // Different parent domain (staging/localhost): redeem the voucher for a
      // fresh refresh token scoped to this host.
      if (!code) {
        throw new Error('Missing auth code.');
      }

      if (!codeVerifier) {
        throw new Error(
          'PKCE verifier cookie missing — re-initiate the login flow.',
        );
      }

      const exchangeResponse = await postJsonAsync(
        `${this.apiUrl}/api/auth/v1/exchange-auth-code`,
        { code, code_verifier: codeVerifier },
      );
      if (!exchangeResponse.ok) {
        throw new Error(
          `Auth code exchange failed: ${exchangeResponse.status}`,
        );
      }

      const { refreshToken } = (await exchangeResponse.json()) as {
        refreshToken: string;
      };
      await this.setRefreshTokenAsync(refreshToken);
    }

    // Same-origin redirect only. `startsWith('/')` alone isn't enough —
    // `//evil.com` and `/\evil.com` are protocol-relative and resolve
    // cross-origin via `new URL()`. Compare the resolved origin against the
    // current origin and fall back to '/' on any mismatch.
    if (redirectPath) {
      // A malformed redirectPath parses to null and falls through to the default.
      const candidate = parseUrl(redirectPath, window.location.origin);
      if (candidate?.origin === window.location.origin) {
        return `${candidate.pathname}${candidate.search}${candidate.hash}`;
      }
    }

    return '/';
  }

  async logOutAsync(): Promise<void> {
    const refreshToken = await this.getRefreshTokenAsync();
    if (refreshToken) {
      try {
        const response = await postJsonAsync(
          `${this.apiUrl}/api/auth/v1/invalidate-refresh-token`,
          { refreshToken },
        );
        if (!response.ok) {
          console.error(
            `Refresh token invalidation failed: ${response.status}`,
          );
        }
      } catch (err) {
        console.error(err);
      }
    }

    this.setCachedAccessToken(null);
    await this.deleteRefreshTokenAsync();
    window.location.reload();
  }

  /** Drop the cached access token so the next getAccessTokenAsync fetches a
   * fresh one. For callers the server has already told the token is dead
   * (UNAUTHORIZED + ACCESS_TOKEN_INVALID): the server's verdict overrules
   * the local expiry heuristics, which can keep vouching for a stale token
   * (clock skew). */
  clearCachedAccessToken() {
    this.setCachedAccessToken(null);
  }

  /**
   * The Domain attribute only applies while the page is served from that
   * domain. Anywhere else — localhost, including `dev:staging` and
   * `dev:production` — the browser would silently reject the write, so the
   * cookie stays host-only instead.
   */
  private getCookieDomain() {
    if (!this.cookieDomain) {
      return undefined;
    }

    const { hostname } = window.location;
    const isOnCookieDomain =
      hostname.endsWith(this.cookieDomain) ||
      hostname === this.cookieDomain.slice(1);

    return isOnCookieDomain ? this.cookieDomain : undefined;
  }

  private getCachedAccessToken() {
    if (typeof sessionStorage === 'undefined') {
      return {
        accessToken: null,
        accessTokenUpdatedAt: null,
      };
    }

    return {
      accessToken: sessionStorage.getItem(ACCESS_TOKEN_KEY),
      accessTokenUpdatedAt: sessionStorage.getItem(ACCESS_TOKEN_UPDATED_AT),
    };
  }

  private setCachedAccessToken(accessToken: string | null) {
    if (typeof sessionStorage === 'undefined') {
      return;
    }

    if (accessToken) {
      sessionStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
      sessionStorage.setItem(ACCESS_TOKEN_UPDATED_AT, new Date().toISOString());
    } else {
      sessionStorage.removeItem(ACCESS_TOKEN_KEY);
      sessionStorage.removeItem(ACCESS_TOKEN_UPDATED_AT);
    }
  }
}
